import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { getUserRole, USER_ROLES } from '../utils/rolePermissions';
import '../styles/Modal.css';
import '../styles/QuickActionsEditModal.css';

const QuickActionsEditModal = ({ isOpen, onClose, onSave, user }) => {
  const [selectedActions, setSelectedActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const userRole = getUserRole(user);

  // Get all available actions for this role
  const getAllActions = () => {
    switch (userRole) {
      case USER_ROLES.SUPER_ADMIN:
        return [
          { id: 'activity-logs', label: 'Activity Logs', icon: 'superadmin', section: 'activity-log' },
          { id: 'create-agency', label: 'Create Agency', icon: 'superadmin', section: 'create-agency' },
          { id: 'organizations', label: 'Organizations & Brands', icon: 'building', section: 'admin-organizations-brands' },
          { id: 'proxy-user', label: 'Proxy User', icon: 'superadmin', section: 'proxy-user' },
          { id: 'user-management', label: 'User Management', icon: 'superadmin', section: 'admin-user-management' },
          { id: 'database-backups', label: 'Database Backups', icon: 'server', section: 'database-backups' },
          { id: 'prompt-hub', label: 'Prompt Hub', icon: 'superadmin', section: 'prompt-hub' },
          { id: 'casper', label: 'Casper', icon: 'superadmin', section: 'casper' },
          { id: 'guest-completions', label: 'Guest Completions', icon: 'superadmin', section: 'guest-completions' },
          { id: 'system-logs', label: 'System Logs', icon: 'superadmin', section: 'logs' }
        ];
      case USER_ROLES.ADMIN:
        return [
          { id: 'activity-logs', label: 'Activity Logs', icon: 'admin', section: 'activity-log' },
          { id: 'create-content', label: 'Create Content', icon: 'pencil', section: 'create' },
          { id: 'organizations', label: 'Organizations & Brands', icon: 'building', section: 'admin-organizations-brands' },
          { id: 'proxy-user', label: 'Proxy User', icon: 'admin', section: 'proxy-user' },
          { id: 'user-management', label: 'User Management', icon: 'admin', section: 'admin-user-management' },
          { id: 'database-backups', label: 'Database Backups', icon: 'server', section: 'database-backups' }
        ];
      case USER_ROLES.OPERATOR:
        return [
          { id: 'brand-identity', label: 'Brand Identity', icon: 'vkontakte-rect', section: 'assets' },
          { id: 'create-content', label: 'Create Content', icon: 'pencil', section: 'create' },
          { id: 'recordings', label: 'Recordings', icon: 'lightbulb', section: 'recordings' },
          { id: 'review-content', label: 'Review Content', icon: 'pencil', section: 'review-content' },
          { id: 'schedule-posts', label: 'Schedule Posts', icon: 'pencil', section: 'schedule-posts' },
          { id: 'create-ideas', label: 'Create Ideas', icon: 'lightbulb', section: 'create-ideas' },
          { id: 'review-ideas', label: 'Review Ideas', icon: 'lightbulb', section: 'operator-review-ideas' },
          { id: 'manage-sources', label: 'Manage Sources', icon: 'sources', section: 'sources' }
        ];
      case USER_ROLES.CLIENT:
        return [
          { id: 'brand-voice', label: 'Brand Voice Designer', icon: 'vkontakte-rect', section: 'brand-builder' },
          { id: 'calendar', label: 'Meeting Calendar', icon: 'calendar', section: 'calendar' },
          { id: 'review-content', label: 'Review Content', icon: 'pencil', section: 'review-content' },
          { id: 'client-review-ideas', label: 'Review Ideas', icon: 'lightbulb', section: 'client-review-ideas' }
        ];
      default:
        return [];
    }
  };

  const allActions = getAllActions();

  useEffect(() => {
    if (isOpen) {
      fetchUserPreferences();
    }
  }, [isOpen]);

  const fetchUserPreferences = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/user/quick-actions-preferences', {
        withCredentials: true
      });

      if (response.data.preferences && response.data.preferences.length > 0) {
        setSelectedActions(response.data.preferences);
      } else {
        // Default to first 5 actions if no preferences saved
        setSelectedActions(allActions.slice(0, 5).map(a => a.id));
      }
    } catch (error) {
      console.error('Error fetching quick actions preferences:', error);
      // Default to first 5 actions on error
      setSelectedActions(allActions.slice(0, 5).map(a => a.id));
    } finally {
      setLoading(false);
    }
  };

  const toggleAction = (actionId) => {
    setSelectedActions(prev => {
      if (prev.includes(actionId)) {
        // Allow deselection
        return prev.filter(id => id !== actionId);
      } else {
        // Only allow selection if less than 5 are selected
        if (prev.length >= 5) {
          return prev; // Don't add, already at max
        }
        return [...prev, actionId];
      }
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await axios.post('/api/user/quick-actions-preferences', {
        preferences: selectedActions
      }, {
        withCredentials: true
      });

      onSave(selectedActions);
      onClose();
    } catch (error) {
      console.error('Error saving quick actions preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-medium">
        <div className="modal-header">
          <h3 className="modal-title">Customize Quick Actions</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close" />
        </div>

        <div className="modal-body quick-actions-modal-body">
          <p className="quick-actions-modal-description">
            Select up to 5 actions you want to appear in your Quick Actions panel on the dashboard.
          </p>

          <div className="quick-actions-list">
            {loading ? (
              <div className="quick-actions-loading">Loading...</div>
            ) : (
              allActions.map((action) => {
                const isSelected = selectedActions.includes(action.id);
                const isDisabled = !isSelected && selectedActions.length >= 5;

                return (
                  <div
                    key={action.id}
                    className={`quick-action-checkbox-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && toggleAction(action.id)}
                  >
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => !isDisabled && toggleAction(action.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="action-info">
                    <Icon name={action.icon} size="1.25rem" />
                    <span className="action-label">{action.label}</span>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn modal-btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-confirm"
            onClick={handleSave}
            disabled={loading || selectedActions.length === 0 || selectedActions.length > 5}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickActionsEditModal;
